<template>
  <v-main>
    <v-container fluid>
      <!-- Loading State -->
      <v-row v-if="isLoading">
        <v-col cols="12">
          <v-card elevation="3" class="form-card">
            <v-card-text class="py-12 text-center">
              <health-loader message="Loading product variant..." />
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Error State -->
      <v-row v-else-if="!productVariant && !isLoading">
        <v-col cols="12">
          <v-card elevation="3" class="form-card">
            <v-card-text class="py-12 text-center">
              <v-icon size="80" color="error">mdi-alert-circle</v-icon>
              <h3 class="text-h6 grey--text mt-4 mb-2">
                Product Variant Not Found
              </h3>
              <p class="grey--text mb-4">
                The requested product variant could not be found or may have
                been deleted.
              </p>
              <v-btn color="primary" @click="$router.back()">
                <v-icon left>mdi-arrow-left</v-icon>
                Go Back
              </v-btn>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Professional Form Card -->
      <v-row v-else>
        <v-col cols="12">
          <v-card elevation="3" class="form-card">
            <!-- Page Header integrated into Form Card -->
            <v-card-title class="page-header py-2 px-3">
              <v-row class="w-100 ma-0" align="center">
                <!-- Left Side: Title and Description -->
                <v-col cols="12" md="8" class="pa-0">
                  <div class="d-flex align-center">
                    <div>
                      <h1 class="page-title primary--text">
                        {{ positionName === 'SELLER' ? 'View Product Variant' : 'Edit Product Variant' }}
                      </h1>
                      <p class="page-subtitle grey--text text--darken-1 mb-0">
                        {{ positionName === 'SELLER' ? 'View product variant details and specifications' : 'Update product variant details and manage specifications' }}
                      </p>
                    </div>
                  </div>
                </v-col>

                <!-- Right Side: Edit Toggle and Back Button -->
                <v-col cols="12" md="4" class="pa-0">
                  <div class="d-flex align-center justify-end">
                    <v-btn
                      v-if="positionName != 'SELLER'"
                      :color="isEditMode ? 'warning' : 'primary'"
                      :outlined="!isEditMode"
                      @click="toggleEditMode"
                      class="mr-3 edit-toggle-button"
                      :disabled="isLoading"
                      small
                    >
                      <v-icon left>{{
                        isEditMode ? "mdi-eye" : "mdi-pencil"
                      }}</v-icon>
                      {{ isEditMode ? "Read Only" : "Edit Mode" }}
                    </v-btn>
                    <v-chip
                      v-else
                      color="info"
                      outlined
                      small
                      class="mr-3"
                    >
                      <v-icon left x-small>mdi-eye</v-icon>
                      View Only Mode
                    </v-chip>
                    <v-btn
                      color="grey darken-1"
                      outlined
                      @click="$router.back()"
                      class="back-button"
                    >
                      <v-icon left>mdi-arrow-left</v-icon>
                      Back to Product Variants
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
            </v-card-title>

            <v-divider></v-divider>

            <!-- Form Content -->
            <v-card-text class="form-content px-2 py-2">
              <v-card-title class="form-header py-1 px-2">
                <h2 class="form-title">Product Variant Information</h2>
                <v-spacer></v-spacer>
                <v-chip
                  :color="isEditMode ? 'success' : 'warning'"
                  :outlined="!isEditMode"
                  x-small
                >
                  <v-icon left x-small>{{
                    isEditMode ? "mdi-pencil" : "mdi-eye"
                  }}</v-icon>
                  {{ isEditMode ? "Edit Mode" : "Read Only Mode" }}
                </v-chip>
              </v-card-title>

              <v-divider></v-divider>

              <v-form ref="form" v-model="valid" lazy-validation>
                <!-- Product Details Section -->
                <div class="form-section mb-2">
                  <h3 class="section-title mb-1">
                    <v-icon color="primary" class="mr-2"
                      >mdi-package-variant</v-icon
                    >
                    Product Variant Details
                  </h3>

                  <v-row>
                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="form.name"
                        label="Product Variant Name"
                        placeholder="Enter product variant name"
                        outlined
                        :disabled="!isEditMode"
                        :readonly="!isEditMode"
                        :rules="[
                          (v) => !!v || 'Product variant name is required',
                        ]"
                        :error-messages="formErrors.name"
                        class="professional-field"
                        hide-details="auto"
                        required
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-package-variant</v-icon>
                        </template>
                      </v-text-field>
                    </v-col>

                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="form.sku_id"
                        label="SKU ID"
                        placeholder="Enter unique SKU (optional)"
                        outlined
                        :disabled="!isEditMode"
                        :readonly="!isEditMode"
                        :error-messages="formErrors.sku_id"
                        class="professional-field"
                        hide-details="auto"
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-barcode</v-icon>
                        </template>
                      </v-text-field>
                    </v-col>
                  </v-row>
                </div>

                <!-- Product Units & Packing Section -->
                <div class="form-section mb-2">
                  <h3 class="section-title mb-1">
                    <v-icon color="primary" class="mr-2"
                      >mdi-package-variant</v-icon
                    >
                    Units & Packaging
                  </h3>

                  <v-row>
                    <v-col cols="12" md="6">
                      <v-autocomplete
                        v-model="form.unit_id"
                        :items="units"
                        item-text="name"
                        item-value="id"
                        label="Unit Type"
                        placeholder="Select unit (e.g., Tablet, ml, gm)"
                        outlined
                        :disabled="!isEditMode"
                        :readonly="!isEditMode"
                        :error-messages="formErrors.unit_id"
                        class="professional-field"
                        hide-details="auto"
                        :clearable="isEditMode"
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-scale</v-icon>
                        </template>
                      </v-autocomplete>
                    </v-col>

                    <v-col cols="12" md="6">
                      <v-autocomplete
                        v-model="form.packing_type_id"
                        :items="packingTypes"
                        item-text="name"
                        item-value="id"
                        label="Packing Type"
                        placeholder="Select packing (e.g., Box, Bottle)"
                        outlined
                        :disabled="!isEditMode"
                        :readonly="!isEditMode"
                        :error-messages="formErrors.packing_type_id"
                        class="professional-field"
                        hide-details="auto"
                        :clearable="isEditMode"
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-package</v-icon>
                        </template>
                      </v-autocomplete>
                    </v-col>
                  </v-row>

                  <v-row>
                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="form.potency"
                        label="Potency"
                        placeholder="e.g. 30C, 200CH, 1M"
                        outlined
                        :disabled="!isEditMode"
                        :readonly="!isEditMode"
                        :error-messages="formErrors.potency"
                        class="professional-field"
                        hide-details="auto"
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-flask</v-icon>
                        </template>
                      </v-text-field>
                    </v-col>
                  </v-row>

                  <v-row>
                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="form.commission"
                        label="Commission (%)"
                        placeholder="Enter commission percentage"
                        outlined
                        type="number"
                        step="0.01"
                        min="0"
                        max="100"
                        :disabled="!isEditMode"
                        :readonly="!isEditMode"
                        :error-messages="formErrors.commission"
                        class="professional-field"
                        hide-details="auto"
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-percent</v-icon>
                        </template>
                      </v-text-field>
                    </v-col>
                  </v-row>
                </div>

                <!-- Physical Dimensions Section -->
                <div class="form-section mb-2">
                  <h3 class="section-title mb-1">
                    <v-icon color="primary" class="mr-2"
                      >mdi-cube-outline</v-icon
                    >
                    Physical Dimensions
                  </h3>

                  <v-row>
                    <v-col cols="12" md="3">
                      <v-text-field
                        v-model="form.length"
                        label="Length (cm)"
                        placeholder="Enter length"
                        outlined
                        type="number"
                        step="0.01"
                        min="0"
                        :disabled="!isEditMode"
                        :readonly="!isEditMode"
                        :error-messages="formErrors.length"
                        class="professional-field"
                        hide-details="auto"
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-ruler</v-icon>
                        </template>
                      </v-text-field>
                    </v-col>

                    <v-col cols="12" md="3">
                      <v-text-field
                        v-model="form.width"
                        label="Width (cm)"
                        placeholder="Enter width"
                        outlined
                        type="number"
                        step="0.01"
                        min="0"
                        :disabled="!isEditMode"
                        :readonly="!isEditMode"
                        :error-messages="formErrors.width"
                        class="professional-field"
                        hide-details="auto"
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-ruler</v-icon>
                        </template>
                      </v-text-field>
                    </v-col>

                    <v-col cols="12" md="3">
                      <v-text-field
                        v-model="form.height"
                        label="Height (cm)"
                        placeholder="Enter height"
                        outlined
                        type="number"
                        step="0.01"
                        min="0"
                        :disabled="!isEditMode"
                        :readonly="!isEditMode"
                        :error-messages="formErrors.height"
                        class="professional-field"
                        hide-details="auto"
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-ruler</v-icon>
                        </template>
                      </v-text-field>
                    </v-col>

                    <v-col cols="12" md="3">
                      <v-text-field
                        v-model="form.weight"
                        label="Weight (grams)"
                        placeholder="Weight including packing"
                        outlined
                        type="number"
                        step="0.01"
                        min="0"
                        :disabled="!isEditMode"
                        :readonly="!isEditMode"
                        :error-messages="formErrors.weight"
                        class="professional-field"
                        hide-details="auto"
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-weight-gram</v-icon>
                        </template>
                      </v-text-field>
                    </v-col>
                  </v-row>
                </div>

                <!-- Product Status Section -->
                <div class="form-section mb-2">
                  <h3 class="section-title mb-1">
                    <v-icon color="primary" class="mr-2">mdi-cog</v-icon>
                    Product Status
                  </h3>

                  <v-row>
                    <v-col cols="12" md="6">
                      <v-card outlined class="status-card">
                        <v-card-text class="pa-2">
                          <div class="d-flex align-center">
                            <v-icon color="grey" class="mr-3"
                              >mdi-package-check</v-icon
                            >
                            <div class="flex-grow-1">
                              <div class="font-weight-medium mb-1">
                                Product Variant Status
                              </div>
                              <div class="text-caption grey--text">
                                Set the availability status for this product
                                variant
                              </div>
                            </div>
                            <v-switch
                              v-model="form.is_active"
                              color="success"
                              inset
                              hide-details
                              class="ml-3"
                              :disabled="!isEditMode"
                            ></v-switch>
                          </div>
                          <div class="mt-2">
                            <v-chip
                              small
                              :color="form.is_active ? 'success' : 'warning'"
                              :text-color="form.is_active ? 'white' : 'black'"
                            >
                              {{ form.is_active ? "Active" : "Inactive" }}
                            </v-chip>
                          </div>
                        </v-card-text>
                      </v-card>
                    </v-col>
                  </v-row>
                </div>

                <!-- Product Analytics Section -->
                <div class="form-section mb-2">
                  <h3 class="section-title mb-1">
                    <v-icon color="primary" class="mr-2">mdi-chart-line</v-icon>
                    Product Analytics
                  </h3>

                  <v-row>
                    <v-col cols="12" md="3">
                      <v-card outlined class="analytics-card">
                        <v-card-text class="pa-2 text-center">
                          <v-icon color="info" class="mb-1"
                            >mdi-calendar-plus</v-icon
                          >
                          <div class="text-caption font-weight-medium mb-1">
                            Created
                          </div>
                          <div class="text-body-2">
                            {{ formatDate(productVariant.created_at) }}
                          </div>
                        </v-card-text>
                      </v-card>
                    </v-col>

                    <v-col cols="12" md="3">
                      <v-card outlined class="analytics-card">
                        <v-card-text class="pa-2 text-center">
                          <v-icon color="warning" class="mb-1"
                            >mdi-calendar-edit</v-icon
                          >
                          <div class="text-caption font-weight-medium mb-1">
                            Last Updated
                          </div>
                          <div class="text-body-2">
                            {{ formatDate(productVariant.updated_at) }}
                          </div>
                        </v-card-text>
                      </v-card>
                    </v-col>

                    <v-col cols="12" md="3">
                      <v-card outlined class="analytics-card">
                        <v-card-text class="pa-2 text-center">
                          <v-icon color="success" class="mb-1">mdi-percent</v-icon>
                          <div class="text-caption font-weight-medium mb-1">
                            Commission
                          </div>
                          <div class="text-body-2 font-weight-bold">
                            {{ productVariant.commission ? productVariant.commission + '%' : 'N/A' }}
                          </div>
                        </v-card-text>
                      </v-card>
                    </v-col>

                    <v-col cols="12" md="3">
                      <v-card outlined class="analytics-card">
                        <v-card-text class="pa-2 text-center">
                          <v-icon :color="productVariant.status ? 'success' : 'error'" class="mb-1">mdi-toggle-switch</v-icon>
                          <div class="text-caption font-weight-medium mb-1">
                            Status
                          </div>
                          <v-chip x-small :color="productVariant.status ? 'success' : 'error'" text-color="white">
                            {{ productVariant.status ? 'Active' : 'Inactive' }}
                          </v-chip>
                        </v-card-text>
                      </v-card>
                    </v-col>
                  </v-row>
                </div>

                <!-- Product Variant Images Section -->
                <ProductVariantImageUpload
                  :product-variant-id="
                    productVariant ? productVariant.id : null
                  "
                  :readonly="!isEditMode"
                  @images-uploaded="handleImagesUploaded"
                  @image-deleted="handleImageDeleted"
                  @images-loaded="handleImagesLoaded"
                />
              </v-form>
            </v-card-text>

            <!-- Form Actions Footer -->
            <v-divider></v-divider>
            <v-card-actions class="form-actions pa-2" v-if="isEditMode && positionName != 'SELLER'">
              <v-row class="w-100 ma-0" align="center">
                <v-col cols="12" md="8" class="pa-0">
                  <div class="d-flex align-center">
                    <v-chip
                      small
                      :color="hasValidationErrors() ? 'error' : 'success'"
                      :text-color="hasValidationErrors() ? 'white' : 'white'"
                      class="mr-3"
                    >
                      <v-icon left x-small>
                        {{
                          hasValidationErrors()
                            ? "mdi-alert-circle"
                            : "mdi-check-circle"
                        }}
                      </v-icon>
                      {{
                        hasValidationErrors()
                          ? "Please fix validation errors"
                          : "Form validation passed"
                      }}
                    </v-chip>
                    <span class="text-caption grey--text">
                      All required fields must be completed
                    </span>
                  </div>
                </v-col>

                <v-col cols="12" md="4" class="pa-0">
                  <div class="d-flex justify-end">
                    <v-btn
                      color="error"
                      outlined
                      class="mr-3 action-button"
                      @click="confirmDelete"
                      :disabled="isSaving || isDeleting"
                    >
                      <v-icon left>mdi-delete</v-icon>
                      Delete
                    </v-btn>
                    <v-btn
                      color="grey"
                      outlined
                      class="mr-3 action-button"
                      @click="$router.back()"
                      :disabled="isSaving || isDeleting"
                    >
                      Cancel
                    </v-btn>
                    <v-btn
                      color="primary"
                      class="action-button"
                      @click="updateProduct"
                      :loading="isSaving"
                      :disabled="isSaving || isDeleting || !valid"
                    >
                      <v-icon left>mdi-content-save</v-icon>
                      Update Product Variant
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>

      <!-- Success Dialog -->
      <v-dialog v-model="showSuccessDialog" width="600">
        <v-card>
          <v-container>
            <v-row align="center">
              <v-col sm="3" align="center">
                <v-icon
                  large
                  style="color: green; font-size: 90px; width: 200px !important"
                >
                  mdi-check-circle
                </v-icon>
              </v-col>
              <v-col sm="9" align="center">
                <v-card-text>
                  <v-row>
                    <v-col>
                      <h1 style="color: green">Success!</h1>
                      <br />
                      <h3>Product variant updated successfully.</h3>
                      <br />
                      <v-btn color="success" @click="showSuccessDialog = false">
                        OK
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-col>
            </v-row>
          </v-container>
        </v-card>
      </v-dialog>

      <!-- Delete Confirmation Dialog -->
      <v-dialog v-model="showDeleteDialog" max-width="500" persistent>
        <v-card>
          <v-card-title class="headline error white--text d-flex align-center">
            <v-icon color="white" class="mr-2">mdi-alert-circle</v-icon>
            Confirm Delete
          </v-card-title>

          <v-card-text class="pt-4">
            <div class="text-center">
              <v-icon size="64" color="error" class="mb-3"
                >mdi-delete-alert</v-icon
              >
              <h3 class="mb-3">
                Are you sure you want to delete this product variant?
              </h3>

              <div
                class="product-info-card pa-3 ma-2"
                style="background: #f5f5f5; border-radius: 8px"
              >
                <div class="d-flex align-center">
                  <v-avatar size="48" class="mr-3" color="primary">
                    <v-icon color="white" size="24">mdi-package-variant</v-icon>
                  </v-avatar>
                  <div class="text-left">
                    <div class="font-weight-bold text-body-1">
                      {{ productVariant ? productVariant.name : "" }}
                    </div>
                    <div class="text-caption grey--text">
                      SKU: {{ productVariant ? (productVariant.sku_id || 'N/A') : 'N/A' }}
                    </div>
                    <div class="text-caption grey--text">
                      Commission: {{ productVariant ? (productVariant.commission ? productVariant.commission + '%' : 'N/A') : 'N/A' }}
                    </div>
                  </div>
                </div>
              </div>

              <p class="text-body-2 grey--text mt-3 mb-1">
                <strong>Warning:</strong> This action will permanently delete
                the product variant.
              </p>
              <p class="text-body-2 grey--text">
                This action cannot be undone. All product variant data will be
                lost.
              </p>
            </div>
          </v-card-text>

          <v-card-actions class="px-4 pb-4">
            <v-spacer></v-spacer>
            <v-btn
              color="grey darken-1"
              text
              @click="showDeleteDialog = false"
              :disabled="isDeleting"
            >
              <v-icon left>mdi-close</v-icon>
              Cancel
            </v-btn>
            <v-btn
              color="error"
              @click="deleteProduct"
              :loading="isDeleting"
              :disabled="isDeleting"
            >
              <v-icon left>mdi-delete</v-icon>
              Delete Product Variant
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-container>
  </v-main>
</template>

<script>
import axios from "@/axios";
import ProductVariantImageUpload from "@/components/ProductVariantImageUpload.vue";

export default {
  components: {
    ProductVariantImageUpload,
  },
  name: "ProductVariantEditPage",
  data() {
    return {
      valid: false,
      productVariant: null,
      form: {
        name: "",
        sku_id: "",
        unit_id: null,
        packing_type_id: null,
        potency: "",
        commission: "",
        length: "",
        width: "",
        height: "",
        weight: "",
        remark: "",
        status: true,
        is_active: true,
      },
      formErrors: {},
      isLoading: true,
      isSaving: false,
      isDeleting: false,
      showSuccessDialog: false,
      showDeleteDialog: false,
      units: [],
      packingTypes: [],
      productImages: [],
      productVariantImages: [],
      isEditMode: false,
    };
  },

  computed: {
    positionName() {
      return this.$store.getters["auth/user"]?.position?.name || "";
    },
  },

  mounted() {
    this.fetchProduct();
    this.getMasters();

    // Check if edit mode should be enabled from query parameter
    // Sellers are always in read-only mode
    if (this.$route.query.edit === "true" && this.positionName !== 'SELLER') {
      this.isEditMode = true;
    }
  },

  methods: {
    hasValidationErrors() {
      return Object.keys(this.formErrors).length > 0 || !this.valid;
    },

    toggleEditMode() {
      // Sellers cannot toggle edit mode
      if (this.positionName === 'SELLER') {
        this.$toast.warning("You don't have permission to edit product variants.");
        return;
      }
      
      this.isEditMode = !this.isEditMode;
      // Show a message to the user about the mode change
      if (this.isEditMode) {
        this.$toast.success("Edit mode enabled. You can now modify the form.");
      } else {
        this.$toast.info(
          "Read-only mode enabled. Form is now protected from changes."
        );
      }
    },

    async fetchProduct() {
      this.isLoading = true;
      try {
        const productVariantId = this.$route.params.productVariantId;
        const response = await axios.get(
          `/product_variants/${productVariantId}`
        );

        if (response.data.success) {
          this.productVariant = response.data.data;
          this.form = {
            name: this.productVariant.name || "",
            sku_id: this.productVariant.sku_id || "",
            unit_id: this.productVariant.unit_id,
            packing_type_id: this.productVariant.packing_type_id,
            potency: this.productVariant.potency || "",
            commission: this.productVariant.commission || "",
            length: this.productVariant.length || "",
            width: this.productVariant.width || "",
            height: this.productVariant.height || "",
            weight: this.productVariant.weight || "",
            remark: this.productVariant.remark || "",
            status: this.productVariant.status,
            is_active: this.productVariant.is_active,
          };
        } else {
          this.$store.dispatch("errors/setError", {
            message: "Product not found",
          });
        }
      } catch (error) {
        console.error(error);
        if (error.response && error.response.status === 404) {
          this.$store.dispatch("errors/setError", {
            message: "Product not found",
          });
        } else if (error.response && error.response.status === 401) {
          this.$router.push("/login");
        } else {
          this.$store.dispatch("errors/setError", {
            message: "Error loading product variant data",
          });
        }
      } finally {
        this.isLoading = false;
      }
    },

    async updateProduct() {
      if (!this.$refs.form.validate()) {
        return;
      }

      this.isSaving = true;
      this.formErrors = {};

      try {
        const productData = {
          name: this.form.name,
          sku_id: this.form.sku_id || null,
          unit_id: this.form.unit_id,
          packing_type_id: this.form.packing_type_id,
          potency: this.form.potency || null,
          commission: this.form.commission ? parseFloat(this.form.commission) : null,
          length: this.form.length ? parseFloat(this.form.length) : null,
          width: this.form.width ? parseFloat(this.form.width) : null,
          height: this.form.height ? parseFloat(this.form.height) : null,
          weight: this.form.weight ? parseFloat(this.form.weight) : null,
          remark: this.form.remark || null,
          status: this.form.status,
          is_active: this.form.is_active,
        };

        const productVariantId = this.$route.params.productVariantId;
        const response = await axios.put(
          `/product_variants/${productVariantId}`,
          productData
        );

        if (response.data.success) {
          this.showSuccessDialog = true;
          this.productVariant = response.data.data;
        } else {
          this.$store.dispatch("errors/setError", {
            message:
              response.data.message ||
              "An error occurred while updating the product variant.",
          });
        }
      } catch (error) {
        console.error(error);

        if (error.response) {
          if (error.response.status === 422) {
            this.formErrors = error.response.data.errors || {};
          } else if (error.response.status === 401) {
            this.$router.push("/login");
          } else {
            this.$store.dispatch("errors/setError", {
              message:
                error.response.data.message ||
                "An error occurred while updating the product variant.",
            });
          }
        } else {
          this.$store.dispatch("errors/setError", {
            message:
              "Network error. Please check your connection and try again.",
          });
        }
      } finally {
        this.isSaving = false;
      }
    },

    confirmDelete() {
      this.showDeleteDialog = true;
    },

    async deleteProduct() {
      this.isDeleting = true;

      try {
        const productVariantId = this.$route.params.productVariantId;
        const response = await axios.post(
          `/product_variants/delete/${productVariantId}`
        );

        if (response.data.success) {
          this.showDeleteDialog = false;
          this.$router.push(
            `/products/${this.$route.params.productId}/product-variants`
          );
        } else {
          this.$store.dispatch("errors/setError", {
            message:
              response.data.message ||
              "An error occurred while deleting the product variant.",
          });
        }
      } catch (error) {
        console.error(error);

        if (error.response && error.response.status === 401) {
          this.$router.push("/login");
        } else {
          this.$store.dispatch("errors/setError", {
            message: "An error occurred while deleting the product variant.",
          });
        }
      } finally {
        this.isDeleting = false;
      }
    },

    formatDate(dateString) {
      if (!dateString) return "N/A";
      const date = new Date(dateString);
      return date.toLocaleDateString() + " " + date.toLocaleTimeString();
    },

    formatPrice(price) {
      return parseFloat(price).toFixed(2);
    },

    getStockStatus(stock) {
      if (stock == 0) return "Out of Stock";
      if (stock <= 10) return "Low Stock";
      return "In Stock";
    },

    getStockStatusColor(stock) {
      if (stock == 0) return "error";
      if (stock <= 10) return "warning";
      return "success";
    },

    async getMasters() {
      try {
        const response = await axios.get("masters?context=product_variants");
        const masters = response.data;

        // Units
        if (masters.units) {
          this.units = masters.units.map((unit) => ({
            id: unit.id,
            name: unit.description,
            value: unit.id,
            text: unit.description,
          }));
        }

        // Packing Types
        if (masters.packing_types) {
          this.packingTypes = masters.packing_types.map((packingType) => ({
            id: packingType.id,
            name: packingType.description,
            value: packingType.id,
            text: packingType.description,
          }));
        }
      } catch (error) {
        console.log("Error loading masters data:", error);
      }
    },

    // Product Variant Image Event Handlers
    handleImagesUploaded(uploadedImages) {
      console.log("Images uploaded:", uploadedImages);
      this.$store.dispatch("errors/setError", {
        message: `${uploadedImages.length} image(s) uploaded successfully`,
        type: "success",
      });
    },

    handleImageDeleted(deletedImage) {
      console.log("Image deleted:", deletedImage);
      // Success message is already handled by the component
    },

    handleImagesLoaded(loadedImages) {
      console.log("Images loaded:", loadedImages);
      // Store images in component state if needed for other operations
      this.productVariantImages = loadedImages;
    },
  },

  watch: {
    // Clear errors when user starts typing
    "form.name"() {
      if (this.formErrors.name) {
        this.$delete(this.formErrors, "name");
      }
    },
    "form.commission"() {
      if (this.formErrors.commission) {
        this.$delete(this.formErrors, "commission");
      }
    },
  },
};
</script>

<style scoped>
/* Professional Page Header */
.page-header-card {
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
  border-left: 4px solid var(--v-primary-base);
}

.page-title {
  font-size: 1.25rem;
  font-weight: 600;
  line-height: 1.1;
  margin-bottom: 0.15rem;
}

.page-subtitle {
  font-size: 0.75rem;
  line-height: 1.2;
}

.back-button {
  min-width: 100px;
  height: 32px;
  font-weight: 500;
  text-transform: none;
  font-size: 0.75rem;
}

.edit-toggle-button {
  min-width: 100px;
  height: 32px;
  font-weight: 500;
  text-transform: none;
  font-size: 0.75rem;
}

/* Professional Form Card */
.form-card {
  background: #ffffff;
  border-radius: 8px;
  overflow: hidden;
}

.form-header {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-bottom: 1px solid #dee2e6;
}

.form-title {
  font-size: 1rem;
  font-weight: 600;
  color: var(--v-primary-base);
  margin: 0;
}

.form-content {
  background: #ffffff;
}

/* Section Styling */
.form-section {
  border-bottom: 1px solid #f0f0f0;
  padding-bottom: 0.5rem;
}

.form-section:last-child {
  border-bottom: none;
  margin-bottom: 0;
  padding-bottom: 0;
}

.section-title {
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--v-primary-base);
  display: flex;
  align-items: center;
  margin-bottom: 0.5rem;
}

/* Professional Form Fields */
.professional-field >>> .v-input__control {
  min-height: 40px;
}

.professional-field >>> .v-text-field__details {
  padding-top: 4px;
}

.professional-field >>> .v-input__slot {
  border-radius: 4px;
}

.professional-field >>> .v-text-field input {
  font-size: 0.75rem;
  padding-top: 8px;
  padding-bottom: 8px;
}

.professional-field >>> .v-label {
  font-size: 0.75rem;
  font-weight: 500;
}

.professional-field >>> .v-icon {
  color: #6c757d !important;
}

.professional-field >>> .v-textarea textarea {
  font-size: 0.75rem;
  line-height: 1.4;
}

/* Status Card */
.status-card {
  border: 2px solid #e9ecef;
  border-radius: 8px;
  transition: all 0.2s ease;
}

.status-card:hover {
  border-color: var(--v-primary-base);
  background-color: #f8f9fa;
}

/* Analytics Cards */
.analytics-card {
  border: 2px solid #e9ecef;
  border-radius: 8px;
  transition: all 0.2s ease;
  min-height: 100px;
}

.analytics-card:hover {
  border-color: var(--v-info-base);
  background-color: #f8f9fa;
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

/* Form Actions */
.form-actions {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-top: 1px solid #dee2e6;
}

.action-button {
  min-width: 90px;
  height: 32px;
  font-weight: 500;
  text-transform: none;
  border-radius: 4px;
  font-size: 0.75rem;
}

/* Spacing and Alignment */
.v-row {
  margin-left: -6px;
  margin-right: -6px;
}

.v-row > .v-col {
  padding: 0 6px;
}

/* Professional Color Scheme */
.v-card {
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08) !important;
}

.v-btn.v-btn--outlined {
  border: 2px solid currentColor;
}

.v-chip {
  font-weight: 500;
}

/* Responsive Design */
@media (max-width: 960px) {
  .page-title {
    font-size: 1.1rem;
  }

  .form-title {
    font-size: 0.9rem;
  }

  .section-title {
    font-size: 0.85rem;
  }

  .form-content {
    padding: 0.75rem !important;
  }

  .back-button,
  .action-button {
    min-width: 80px;
    font-size: 0.7rem;
  }
}

@media (max-width: 600px) {
  .v-row > .v-col {
    padding: 0 4px;
  }

  .form-content {
    padding: 0.5rem !important;
  }

  .section-title {
    font-size: 0.8rem;
  }

  .professional-field >>> .v-input__control {
    min-height: 36px;
  }

  .professional-field >>> .v-text-field input {
    font-size: 0.7rem;
    padding-top: 6px;
    padding-bottom: 6px;
  }

  .back-button,
  .action-button {
    min-width: 70px;
    height: 28px;
    font-size: 0.65rem;
  }
}

/* Clean Focus States */
.professional-field >>> .v-input--is-focused .v-input__slot {
  border-color: var(--v-primary-base) !important;
  border-width: 2px !important;
}

/* Consistent Margins */
.mb-1 {
  margin-bottom: 0.5rem !important;
}

.mb-2 {
  margin-bottom: 0.75rem !important;
}

.mb-3 {
  margin-bottom: 1rem !important;
}

/* Professional Transitions */
.v-card,
.v-btn,
.professional-field,
.status-card,
.analytics-card {
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

/* Delete Confirmation Dialog Styling */
.product-info-card {
  border: 1px solid #e0e0e0;
  transition: all 0.2s ease;
}

.product-info-card:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

/* Vue Editor Styling */
.editor-field-container {
  margin-bottom: 1rem;
}

.editor-label {
  display: flex;
  align-items: center;
  font-size: 0.875rem;
  font-weight: 600;
  color: #424242;
  margin-bottom: 0.5rem;
}

.professional-editor >>> .quill {
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  font-size: 0.875rem;
}

.professional-editor >>> .ql-editor {
  min-height: 120px;
  font-size: 0.875rem;
  line-height: 1.5;
  padding: 12px 15px;
}

.professional-editor >>> .ql-toolbar {
  border-bottom: 1px solid #e0e0e0;
  background: #fafafa;
}

.professional-editor >>> .ql-toolbar .ql-stroke {
  fill: none;
  stroke: #666;
}

.professional-editor >>> .ql-toolbar .ql-fill {
  fill: #666;
  stroke: none;
}

.professional-editor >>> .ql-toolbar .ql-picker-label {
  color: #666;
}

.professional-editor.error--text >>> .quill {
  border-color: #ff5252;
}

.read-only-content {
  min-height: 120px;
  padding: 12px 15px;
  background: #f9f9f9;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  font-size: 0.875rem;
  line-height: 1.5;
  color: #424242;
}

.read-only-content p {
  margin-bottom: 0.5rem;
}

.read-only-content p:last-child {
  margin-bottom: 0;
}
</style>